import { Box, createStyles, Group } from '@mantine/core';
import { ListBox, Text } from '@repo/foundations';
import { space } from '@repo/theme/primitives';
import { memo, useCallback, useMemo, useState } from 'react';
import { useSecodaEntity, useUpdateSecodaEntity } from '../../api';
import { useCheckEntityUpdatePermission } from '../../utils/authorization/roles';
import { AddGlossarySynonym } from './AddGlossarySynonym';
import { GlossarySynonymItem } from './GlossarySynonymItem';
import { GlossarySynonymPill } from './GlossarySynonymPill';

const useStyles = createStyles((theme) => ({
	wrapper: {
		flex: '1 !important',
		minHeight: space[7],
		maxWidth: '100%',
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing.xs,
		overflow: 'hidden',
		alignItems: 'center',
	},
	overflowButton: {
		background: theme.other.getColor('fill/transparent-secondary/default'),
		borderRadius: theme.radius.sm,
		height: space[5],
		border: 'none',
		cursor: 'pointer',
		display: 'flex',
		alignItems: 'center',
		padding: `0 ${theme.spacing['2xs']}`,
	},
}));

export interface InlineGlossarySynonymsProps {
	glossaryTermId: string;
}

// I tried to do this dynamically based on the height of the container, but it was too complicated to implement.
// see https://github.com/secoda/secoda/blob/43835bd4f3682d1da0f843d66971b23fb5b37b7e/apps/frontend/src/hooks/useOverflowCalculation.ts
const MAX_VISIBLE_ITEMS = 7;

export const InlineGlossarySynonyms = memo(
	({ glossaryTermId }: InlineGlossarySynonymsProps) => {
		const { classes } = useStyles();
		const [popoverOpened, setPopoverOpened] = useState(false);
		const [searchTerm, setSearchTerm] = useState('');

		const { data: glossaryTerm } = useSecodaEntity({
			id: glossaryTermId,
			options: {
				enabled: !!glossaryTermId,
				meta: {
					normalize: false,
				},
			},
		});

		const { mutateAsync: updateGlossaryTerm } = useUpdateSecodaEntity({});

		const editorOfEntity = useCheckEntityUpdatePermission({
			entity: glossaryTerm,
		});

		// Sort synonyms for consistent display
		const sortedSynonyms = useMemo(
			() =>
				(glossaryTerm?.synonyms ?? []).sort((a, b) =>
					a.toLowerCase().localeCompare(b.toLowerCase())
				),
			[glossaryTerm?.synonyms]
		);

		const handleOnAdd = useCallback(
			async (synonym: string) => {
				if (!glossaryTerm) {
					return;
				}

				const synonyms = [...glossaryTerm.synonyms, synonym];
				await updateGlossaryTerm({
					data: {
						id: glossaryTerm.id,
						synonyms,
					},
				});
			},
			[glossaryTerm, updateGlossaryTerm]
		);

		const handleOnRemove = useCallback(
			(synonym: string) => async () => {
				if (!glossaryTerm) {
					return;
				}

				const synonyms = glossaryTerm.synonyms.filter((s) => s !== synonym);
				await updateGlossaryTerm({
					data: {
						id: glossaryTerm.id,
						synonyms,
					},
				});
			},
			[glossaryTerm, updateGlossaryTerm]
		);

		const isEmpty =
			!glossaryTerm?.synonyms || glossaryTerm.synonyms.length === 0;
		const { visibleSynonyms, hiddenSynonyms } = useMemo(() => {
			if (sortedSynonyms.length <= MAX_VISIBLE_ITEMS) {
				return {
					visibleSynonyms: sortedSynonyms,
					hiddenSynonyms: [],
				};
			}

			return {
				visibleSynonyms: sortedSynonyms.slice(0, MAX_VISIBLE_ITEMS),
				hiddenSynonyms: sortedSynonyms.slice(MAX_VISIBLE_ITEMS),
			};
		}, [sortedSynonyms]);

		// Filter hidden synonyms based on search term
		const filteredHiddenSynonyms = useMemo(() => {
			if (!searchTerm.trim()) {
				return hiddenSynonyms;
			}

			return hiddenSynonyms.filter((synonym) =>
				synonym.toLowerCase().includes(searchTerm.toLowerCase())
			);
		}, [hiddenSynonyms, searchTerm]);

		const handleOpenChange = useCallback(
			(newOpened: boolean) => {
				setSearchTerm('');
				setPopoverOpened(newOpened);
			},
			[setSearchTerm]
		);

		return (
			<Group spacing="xs" noWrap align="flex-start">
				<Text w={145} size="sm" py="4xs">
					Synonyms
				</Text>
				<Group align="flex-start" className={classes.wrapper}>
					{visibleSynonyms.map((synonym, index) => (
						<GlossarySynonymPill
							key={`${synonym}-${index}`}
							synonym={synonym}
							onRemove={editorOfEntity ? handleOnRemove(synonym) : undefined}
						/>
					))}

					{hiddenSynonyms.length > 0 && (
						<ListBox
							opened={popoverOpened}
							onOpenChange={handleOpenChange}
							closeOnEscape
						>
							<ListBox.Target>
								<Box
									className={classes.overflowButton}
									onClick={() => setPopoverOpened(!popoverOpened)}
								>
									<Text color="text/secondary/default" size="sm" py="4xs">
										+{hiddenSynonyms.length} more
									</Text>
								</Box>
							</ListBox.Target>

							<ListBox.ItemsDropdown
								usePortal
								items={filteredHiddenSynonyms}
								renderItem={(synonym, getProps) => (
									<GlossarySynonymItem
										key={synonym}
										synonym={synonym}
										onRemove={
											editorOfEntity ? handleOnRemove(synonym) : undefined
										}
										getProps={getProps}
									/>
								)}
							/>
						</ListBox>
					)}
					{editorOfEntity && (
						<AddGlossarySynonym onAdd={handleOnAdd} isEmpty={isEmpty} />
					)}
				</Group>
			</Group>
		);
	}
);
InlineGlossarySynonyms.displayName = 'InlineGlossarySynonyms';
